import { InfoPopover, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { isNullish } from '@/utils/booleans';

const DEFAULT_ARROW_SIZE = { width: 24, height: 20 };
const DEFAULT_GRADIENT_COLORS =
  'hsl(0, 70%, 45%), hsl(25, 85%, 55%), hsl(50, 90%, 55%), hsl(85, 60%, 40%), hsl(120, 55%, 28%)';
export const DEFAULT_INVERSE_GRADIENT_COLORS =
  'hsl(120, 55%, 28%), hsl(85, 60%, 40%), hsl(50, 90%, 55%), hsl(25, 85%, 55%), hsl(0, 70%, 45%)';
const DEFAULT_GRADIENT_HEIGHT = 16;

type GradientProps = {
  min: number | null | undefined;
  max: number | null | undefined;
  value: number | null | undefined;
  title?: string;
  tooltip?: string;
  unit?: string | UnitEnum;
  arrowSize?: { width: number; height: number };
  gradientColors?: string;
  gradientHeight?: number;
  className?: string;
};

export const GradientScale = ({
  min,
  max,
  value,
  title,
  tooltip,
  unit,
  arrowSize = DEFAULT_ARROW_SIZE,
  gradientColors = DEFAULT_GRADIENT_COLORS,
  gradientHeight = DEFAULT_GRADIENT_HEIGHT,
  className,
}: GradientProps) => {
  const { t } = useTranslation();
  if (isNullish(min) || isNullish(max) || isNullish(value)) {
    return null;
  }

  const displayUnit = Object.values(UnitEnum).includes(unit as UnitEnum)
    ? t(`global.ui.unit.${unit as UnitEnum}`)
    : unit;
  const displayValue = getDisplayNumber(value);

  return (
    <Stack spacing={1} className={className}>
      <Stack direction='row' className='w-full justify-between'>
        <Stack direction='row' className='items-center' spacing={2}>
          {title && <span className='typography-body2 text-text-secondary capitalize'>{title}</span>}
          {tooltip && (
            <Stack direction='row' className='items-center justify-between'>
              <Stack direction='row' className='items-center' spacing={1}>
                {tooltip && <InfoPopover body={tooltip} />}
              </Stack>
            </Stack>
          )}
        </Stack>
        <span className='typography-body2 text-text-secondary'>
          {displayUnit ? `${displayValue} ${displayUnit}` : displayValue}
        </span>
      </Stack>
      <div className='relative' style={{ paddingTop: `calc(${arrowSize.height}px - ${arrowSize.height / 3}px)` }}>
        <GradientArrow value={value} min={min} max={max} arrowSize={arrowSize} />
        <GradientBar gradientColors={gradientColors} gradientHeight={gradientHeight} />
      </div>
    </Stack>
  );
};

const GradientArrow = ({
  value,
  min,
  max,
  arrowSize = DEFAULT_ARROW_SIZE,
}: {
  value: number;
  min: number;
  max: number;
  arrowSize?: { width: number; height: number };
}) => {
  const arrowPosition = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  return (
    <div
      className='absolute'
      style={{
        left: `${arrowPosition}%`,
        top: 0,
        transform: 'translateX(-50%)',
        width: `${arrowSize.width}px`,
        height: `${arrowSize.height}px`,
        backgroundColor: '#9E9E9E',
        clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
      }}
    />
  );
};

const GradientBar = ({
  gradientColors = DEFAULT_GRADIENT_COLORS,
  gradientHeight = DEFAULT_GRADIENT_HEIGHT,
}: {
  gradientColors?: string;
  gradientHeight?: number;
}) => {
  return (
    <div
      className='rounded'
      style={{
        height: `${gradientHeight}px`,
        background: `linear-gradient(to right, ${gradientColors})`,
      }}
    />
  );
};
